import React, { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { message, Button } from 'antd';
import { DASHBOARD_QUERY_STUDENT_NOTES, DASHBOARD_DELETE_STUDENT_NOTE } from '../queries';
import Panel from 'common/components/panel';
import NoteList from 'common/components/note-list';
import NoteModal from '../noteModal';

const StudentNoteList = ({ studentId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const notePageSize = 5;
  const landlordId = useSelector(state => state?.auth?.getIn(['base', 'landlordId']));
  const [notePageNumber, setNotePageNumber] = useState(1);
  const [noteHasMore, setNoteHasMore] = useState(true);
  const [notes, setNotes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteId, setNoteId] = useState(null);

  const [queryNotes, { loading: notesLoading, data: noteData }] = useLazyQuery(
    DASHBOARD_QUERY_STUDENT_NOTES,
    {
      variables: {
        id: studentId,
        pageNumber: notePageNumber,
        pageSize: notePageSize,
        sortBy: 'CREATED_AT',
      },
      fetchPolicy: 'no-cache',
      onError: err => {
        message.error(err.message?.toString());
      },
    },
  );

  const [deleteStudentNote] = useMutation(DASHBOARD_DELETE_STUDENT_NOTE, {
    onError: err => {
      message.error(err.message?.toString());
    },
    onCompleted: () => {
      message.success(t('cms:cms.students.list.modal.note_delete_success'));
      setNotes([]);
      setNoteHasMore(true);
      setNotePageNumber(1);
      queryNotes();
    },
  });

  const handleLoadMore = () => {
    if (!noteHasMore) return;
    setNotePageNumber(prev => prev + 1);
    queryNotes();
  };

  const handleAddNote = e => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
    setNoteInput('');
    setNoteId(null);
  };

  const handleAddNoteClose = () => {
    setIsModalOpen(false);
  };

  const handleAddedNote = () => {
    setIsModalOpen(false);
    setNotes([]);
    setNotePageNumber(1);
    queryNotes();
  };

  const handleDeleteNote = id => {
    deleteStudentNote({
      variables: { input: { id } },
    });
  };

  const handleEditNote = (id, note) => {
    setIsModalOpen(true);
    setNoteInput(note);
    setNoteId(id);
  };

  const handleUpdatedNote = () => {
    setIsModalOpen(false);
    setNoteId(null);
    setNotes([]);
    setNotePageNumber(1);
    queryNotes();
  };

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    const result = noteData?.listStudents?.edges?.[0]?.node?.studentNotes?.edges || [];
    const resultLength = result.length;
    const totalCount = noteData?.listStudents?.edges?.[0]?.node?.studentNotes?.totalCount || 0;
    setNoteHasMore(totalCount > notes.length + resultLength);
    if (resultLength) {
      setNotes(prev => [...prev, ...result.map(item => item.node)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData]);

  useEffect(() => {
    if (studentId) {
      setNotePageNumber(1);
      setNoteHasMore(true);
      setNotes([]);
      queryNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  return (
    <div>
      <Panel
        isEmpty={notes.length === 0 && !notesLoading}
        emptyContent={
          <div className="student-detail__panel-empty">
            {t('cms:cms.students.detail.panel.notes.empty')}
            <Button className="student-detail__add-note-button" onClick={handleAddNote}>
              {t('cms:cms.students.detail.panel.notes.add_new_note')}
            </Button>
          </div>
        }
        title={
          <div id="notes" className="student-detail__panel-title">
            <span>{t('cms:cms.students.detail.panel.notes.title')}</span>
            <button className="student-detail__panel-add-button" onClick={handleAddNote}>
              +
            </button>
          </div>
        }
      >
        <NoteList
          notes={notes}
          hasMore={noteHasMore}
          loading={notesLoading}
          onLoadMore={handleLoadMore}
          onDelete={handleDeleteNote}
          onEdit={handleEditNote}
        />
      </Panel>
      <NoteModal
        value={noteInput}
        isOpen={isModalOpen}
        noteId={noteId}
        studentId={studentId}
        landlordId={landlordId}
        onClose={handleAddNoteClose}
        onAdded={handleAddedNote}
        onUpdated={handleUpdatedNote}
        successMessage={<div>{t('cms:cms.students.list.modal.note_success')}</div>}
      />
    </div>
  );
};

export default memo(StudentNoteList);
